import axios from "axios";
import { jwtDecode } from "jwt-decode";

// Lazy dynamic import to break the circular dependency:
// axios -> userStore -> bearcatApiService -> axios
// The interceptors only run at request time, so the store module
// will be fully initialized by then.
let _userStore = null;
const getUserStore = async () => {
  if (!_userStore) {
    const mod = await import("../store/userStore");
    _userStore = mod.default;
  }
  return _userStore;
};

/**
 * Creates a request interceptor that handles token authentication
 * @param {axios.AxiosInstance} instance - The axios instance to attach the interceptor to
 * @returns {number} The interceptor ID
 */
const createAuthInterceptor = (instance) => {
  return instance.interceptors.request.use(
    async (config) => {
      try {
        const store = await getUserStore();
        const { token, refresh } = store.getState();

        if (token) {
          const decoded = jwtDecode(token);

          if (decoded.exp * 1000 < Date.now()) {
            const newToken = await refresh();
            if (newToken) {
              config.headers.Authorization = `Bearer ${newToken}`;
            }
          } else {
            config.headers.Authorization = `Bearer ${token}`;
          }
        }

        return config;
      } catch (error) {
        console.error("Error in request interceptor:", error);
        return Promise.reject(error);
      }
    },
    (error) => {
      return Promise.reject(error);
    }
  );
};

/**
 * Creates a response interceptor that handles 401 errors
 * @param {axios.AxiosInstance} instance - The axios instance to attach the interceptor to
 * @returns {number} The interceptor ID
 */
const createErrorInterceptor = (instance) => {
  return instance.interceptors.response.use(
    (response) => response,
    async (error) => {
      if (error.response && error.response.status === 401) {
        const store = await getUserStore();
        const { logout } = store.getState();
        await logout();
      }

      return Promise.reject(error);
    }
  );
};

/**
 * Factory function to create axios instances with different base URLs
 * @param {string} baseURL - The base URL for the API
 * @param {Object} options - Additional options for the instance
 * @param {boolean} options.withToken - Whether to include token authentication (default: true)
 * @param {Object} options.customHeaders - Additional custom headers (can override default headers)
 * @returns {axios.AxiosInstance} Configured axios instance
 */
export const createAxiosInstance = (baseURL, options = {}) => {
  const { withToken = true, customHeaders = {} } = options;

  // Default headers applied to all API calls
  // Tenant key is included for multi-tenant backend routing
  const defaultHeaders = {
    "Content-Type": "application/json",
    "X-Tenant-Key": import.meta.env.VITE_TENANT_KEY || "",
  };

  const axiosParams = {
    baseURL,
    headers: {
      ...defaultHeaders,
      // customHeaders come last so they can override defaults if needed
      ...customHeaders,
    },
  };

  const instance = axios.create(axiosParams);

  // Attach interceptors if needed
  if (withToken) {
    createAuthInterceptor(instance);
    createErrorInterceptor(instance);
  }

  return instance;
};
